import type { ReplayAttemptAudit, ReplayEnvelopeV1 } from '@cars/shared';

export interface LapSubmissionCandidate {
  sequence: number;
  claimedTimeMs: number;
  replay?: ReplayEnvelopeV1;
  audit?: ReplayAttemptAudit;
  lapNumber?: number;
}

export type LapSubmissionErrorDisposition = 'retryable' | 'grant-terminal' | 'replay-terminal';

export function lapSubmissionStatusMessage(
  disposition: LapSubmissionErrorDisposition,
  code: string | undefined,
): string {
  if (disposition === 'grant-terminal') return 'Renewing verified lap…';
  if (disposition === 'replay-terminal') {
    return code ? `Verified lap rejected (${code}).` : 'Verified lap rejected.';
  }
  return 'Verified lap temporarily unavailable; will retry.';
}

export function classifyLapSubmissionError(
  status: number,
  code: string | undefined,
): LapSubmissionErrorDisposition {
  if (code === 'attempt_expired' || code === 'attempt_consumed' || code === 'attempt_not_found') {
    return 'grant-terminal';
  }
  if (status === 429 || code === 'attempt_busy' || status >= 500) return 'retryable';
  if (status >= 400 && status < 500) return 'replay-terminal';
  return 'retryable';
}

/** Bounded FIFO retaining immutable evidence for every completed lap. */
export class LapSubmissionCoordinator {
  private nextSequence = 0;
  private readonly pending: LapSubmissionCandidate[] = [];
  /**
   * A candidate removed by take() is still retained by LiveSession while the
   * network request is in flight. Keep that reservation in the same bounds as
   * pending candidates so active evidence cannot disappear from accounting.
   */
  private readonly inFlight = new Map<number, LapSubmissionCandidate>();
  private pendingBytes = 0;
  private inFlightBytes = 0;
  private readonly capacity: number;
  private readonly maxEvidenceBytes: number;
  constructor(capacity = 32, maxEvidenceBytes = 16 * 1024 * 1024) {
    this.capacity = capacity;
    this.maxEvidenceBytes = maxEvidenceBytes;
  }

  queue(
    candidateOrTime: number | Omit<LapSubmissionCandidate, 'sequence'>,
  ): LapSubmissionCandidate {
    if (this.totalCount() >= this.capacity) throw new Error('Lap submission queue is full');
    const candidate: LapSubmissionCandidate = {
      ...(typeof candidateOrTime === 'number'
        ? { claimedTimeMs: candidateOrTime }
        : candidateOrTime),
      sequence: ++this.nextSequence,
    };
    const bytes = evidenceBytes(candidate);
    if (this.totalBytes() + bytes > this.maxEvidenceBytes)
      throw new Error('Lap submission evidence is full');
    this.pending.push(candidate);
    this.pendingBytes += bytes;
    return candidate;
  }

  take(): LapSubmissionCandidate | null {
    const candidate = this.pending.shift() ?? null;
    if (candidate) {
      const bytes = evidenceBytes(candidate);
      this.pendingBytes -= bytes;
      this.inFlight.set(candidate.sequence, candidate);
      this.inFlightBytes += bytes;
    }
    return candidate;
  }

  restore(candidate: LapSubmissionCandidate): void {
    if (this.pending.some((entry) => entry.sequence === candidate.sequence)) return;
    const inFlight = this.inFlight.get(candidate.sequence);
    if (inFlight) {
      this.inFlight.delete(candidate.sequence);
      this.inFlightBytes -= evidenceBytes(inFlight);
    }
    if (this.totalCount() >= this.capacity) throw new Error('Lap submission queue is full');
    const bytes = evidenceBytes(candidate);
    if (this.totalBytes() + bytes > this.maxEvidenceBytes)
      throw new Error('Lap submission evidence is full');
    this.pending.unshift(candidate);
    this.pendingBytes += bytes;
  }

  settle(candidate: LapSubmissionCandidate): void {
    const index = this.pending.findIndex((entry) => entry.sequence === candidate.sequence);
    if (index >= 0) {
      const [removed] = this.pending.splice(index, 1);
      if (removed) this.pendingBytes -= evidenceBytes(removed);
    }
    const inFlight = this.inFlight.get(candidate.sequence);
    if (inFlight) {
      this.inFlight.delete(candidate.sequence);
      this.inFlightBytes -= evidenceBytes(inFlight);
    }
  }

  hasPending(): boolean {
    return this.pending.length > 0;
  }

  clear(): void {
    this.pending.length = 0;
    this.inFlight.clear();
    this.pendingBytes = 0;
    this.inFlightBytes = 0;
  }

  private totalCount(): number {
    return this.pending.length + this.inFlight.size;
  }

  private totalBytes(): number {
    return this.pendingBytes + this.inFlightBytes;
  }
}

function evidenceBytes(candidate: LapSubmissionCandidate): number {
  if (!candidate.replay && !candidate.audit) return 0;
  try {
    return new TextEncoder().encode(
      JSON.stringify({ replay: candidate.replay, audit: candidate.audit }),
    ).byteLength;
  } catch {
    return Number.POSITIVE_INFINITY;
  }
}
